import React, { Fragment, useMemo } from 'react';
import { Typography } from '@vp/swan';
import { ValidationState } from '@vp/upload-and-print-validator';
import { usePerfectBound } from 'src/hooks/usePerfectBound';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { ValidationNames } from 'src/utils/Constants';
import { text } from 'src/utils/localizationUtil';
import { ValidationErrorDataType, validationErrorsMessages } from 'src/utils/validationErrorsMessage';
import { getUnEmbeddedFilePages } from 'src/utils/validationUtil';
import { ValidationCard } from './ValidationCard';

interface Props {
  bodyDescription?: string;
  showCardTitle?: boolean;
}

export const ValidationErrorCardContainer = ({ bodyDescription, showCardTitle }: Props) => {
  const { computedValidations, validationPlaceholders, validations } = useValidationContext();
  const { isPerfectBound, getPerfectBoundPageCountTokenValues } = usePerfectBound();

  const corruptValidator = validations?.find(validation => validation.id === ValidationNames.CorruptValidator);
  const fileValidationMessage = corruptValidator?.message ?? '';
  const unEmbeddedPages = corruptValidator?.details?.pagesData;

  const validationsWithErrorState = (validations ?? validationPlaceholders ?? []).filter(
    validation => validation.state === ValidationState.Error
  );
  const computedValidationsWithErrorState = computedValidations.filter(computedValidation => {
    return computedValidation.state === ValidationState.Error;
  });

  validationsWithErrorState.forEach((item: ValidationErrorDataType) => {
    const additionalData = validationErrorsMessages[item.id as keyof typeof validationErrorsMessages];
    if (additionalData) {
      const hasNonEmbeddedFontsError =
        additionalData.id === ValidationNames.CorruptValidator && unEmbeddedPages.length > 0;
      item.modalTitle = hasNonEmbeddedFontsError
        ? text('validations.non embedded fonts.error-modal-title')
        : additionalData.modalTitle;
      item.errorDescription = hasNonEmbeddedFontsError
        ? text(fileValidationMessage, { pageNumbers: getUnEmbeddedFilePages(unEmbeddedPages) })
        : additionalData.errorDescription;
    }
  });

  const showDescription = useMemo(() => {
    // Check if there are any validations with error state or computed validations
    return validationsWithErrorState.length > 0 || computedValidationsWithErrorState.length > 0;
  }, [validationsWithErrorState.length, computedValidationsWithErrorState.length]);

  if (computedValidationsWithErrorState.length > 0 && validationsWithErrorState.length === 1) {
    return (
      <Fragment>
        {computedValidationsWithErrorState.map(computedValidation => (
          <ValidationCard
            key={computedValidation.id}
            cardTitle={
              showCardTitle
                ? text('template-mismatch-dialog.modal-title', { error: text('template-mismatch-dialog.name') })
                : undefined
            }
            cardMessage={text('template-mismatch-dialog.error-message')}
            type="error"
          />
        ))}
        {bodyDescription ? <Typography mt={3}>{bodyDescription}</Typography> : null}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {validationsWithErrorState.map((item: ValidationErrorDataType) => {
        const tokenValues = isPerfectBound ? getPerfectBoundPageCountTokenValues(item) : item.tokenValues;
        return (
          <ValidationCard
            key={item.id}
            cardTitle={showCardTitle ? text(item.modalTitle || '', item.tokenValues) : undefined}
            cardMessage={text(item.errorDescription || '', tokenValues)}
            type="error"
          />
        );
      })}
      {showDescription && bodyDescription ? <Typography mt={3}>{bodyDescription}</Typography> : null}
    </Fragment>
  );
};
